import React from "react";
import {
  FiCalendar,
  FiEdit2,
  FiExternalLink,
  FiFlag,
  FiMapPin,
  FiMessageCircle,
  FiMoreHorizontal,
  FiSend,
  FiThumbsUp,
  FiTrash2,
  FiX,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import {
  CommentResponseDto,
  EditingPost,
  FileMetadata,
  PostResponseDto,
} from "../../types/types";
import { formatDateTime } from "../../utils/common/formatUtils";
import { VIDEO_UPLOAD_ENABLED } from "../../config/featureFlags";

interface PostCardProps {
  post: PostResponseDto;
  currentUserId: string | number;
  activeTab: "all" | "my";
  likedPosts: { [postId: number]: boolean };
  comments: { [postId: number]: CommentResponseDto[] };
  newComment: { [postId: number]: string };
  expandedComments: { [postId: number]: boolean };
  currentMediaIndex: { [postId: number]: number };
  editingPost: EditingPost | null;
  showOptionsMenu: number | null;
  posts: PostResponseDto[];
  onLike: (postId: number, isLiked: boolean) => void;
  onShowLikes: (postId: number) => void;
  onEdit: (post: PostResponseDto) => void;
  onDelete: () => void;
  onSavePost: (postId: number) => void;
  onAddComment: (e: React.FormEvent, postId: number) => void;
  onDeleteComment: (postId: number, commentId: number) => void;
  onToggleComments: (postId: number) => void;
  onSetNewComment: React.Dispatch<
    React.SetStateAction<{ [postId: number]: string }>
  >;
  onSetShowOptionsMenu: React.Dispatch<React.SetStateAction<number | null>>;
  onSetEditingPost: React.Dispatch<React.SetStateAction<EditingPost | null>>;
  onRemoveSingleMedia: (index: number) => void;
  onSetCurrentMediaIndex: React.Dispatch<
    React.SetStateAction<{ [postId: number]: number }>
  >;
  onOpenReportModal: (postId: number) => void;
  onViewProfile: (profileId: string | number) => void;
  t: (key: string, fallback?: string) => string;
}

const PostCard: React.FC<PostCardProps> = ({
  post,
  currentUserId,
  activeTab,
  likedPosts,
  comments,
  newComment,
  expandedComments,
  currentMediaIndex,
  editingPost,
  showOptionsMenu,
  posts,
  onLike,
  onShowLikes,
  onEdit,
  onDelete,
  onSavePost,
  onAddComment,
  onDeleteComment,
  onToggleComments,
  onSetNewComment,
  onSetShowOptionsMenu,
  onSetEditingPost,
  onRemoveSingleMedia,
  onSetCurrentMediaIndex,
  onOpenReportModal,
  onViewProfile,
  t,
}) => {
  const navigate = useNavigate();
  const isMyPost = String(post.profile.id) === String(currentUserId);
  const files = post.file ? [post.file] : [];
  const currentIndex = currentMediaIndex[post.id] || 0;
  const hasMultipleMedia = files.length > 1;
  const totalReactions = post.totalLikes;
  const hasComments = expandedComments[post.id];
  const eventDetails = post.event;

  let eventLocation = "";
  if (eventDetails?.venue) {
    eventLocation = eventDetails.venue;
  }
  if (eventDetails?.city) {
    eventLocation = eventLocation
      ? `${eventLocation}, ${eventDetails.city}`
      : eventDetails.city;
  }
  if (eventDetails?.country) {
    eventLocation = eventLocation
      ? `${eventLocation}, ${eventDetails.country}`
      : eventDetails.country;
  }

  let eventBoutCount = 0;
  if (eventDetails?.totalBoutCount !== undefined) {
    eventBoutCount = eventDetails.totalBoutCount;
  } else if (eventDetails?.bouts) {
    eventBoutCount = eventDetails.bouts.length;
  }

  let visibleEventBoutCount = 0;
  if (eventDetails?.bouts) {
    visibleEventBoutCount =
      eventDetails.bouts.length > 2 ? 2 : eventDetails.bouts.length;
  }

  const getFighterName = (
    profile?: {
      firstName?: string;
      lastName?: string;
    } | null,
    manualName?: string | null,
  ) => {
    const firstName = profile?.firstName || "";
    const lastName = profile?.lastName || "";
    const fullName = `${firstName} ${lastName}`.trim();
    const fallbackName = String(manualName || "").trim();

    return fullName || fallbackName || "TBD";
  };

  const handleNextMedia = (postId: number, totalMedia: number) => {
    onSetCurrentMediaIndex((prev) => ({
      ...prev,
      [postId]: (prev[postId] + 1) % totalMedia,
    }));
  };

  const handlePrevMedia = (postId: number, totalMedia: number) => {
    onSetCurrentMediaIndex((prev) => ({
      ...prev,
      [postId]: (prev[postId] - 1 + totalMedia) % totalMedia,
    }));
  };

  const renderFormattedContent = (content?: string) => {
    if (!content) return null;
    const words = content.split(/(\s+)/);
    return words.map((word, idx) => {
      if (word.startsWith("@") && word.length > 1) {
        return (
          <span
            key={idx}
            className="text-blue-600 font-medium cursor-pointer hover:underline"
          >
            {word}
          </span>
        );
      }
      return word;
    });
  };

  const renderMedia = (
    file: FileMetadata,
    index: number,
    postId: number,
    variant: "post" | "thumbnail" = "post",
  ) => {
    if (!file?.fileUrl) return null;
    const isVideo = file.fileName?.match(/\.(mp4|webm|ogg|mov)$/i);
    const postFrameClass =
      "w-full overflow-hidden bg-gray-100 rounded-2xl flex items-center justify-center";
    const thumbnailFrameClass =
      "w-full h-full overflow-hidden bg-gray-100 rounded-xl";
    const videoFrameClass =
      variant === "thumbnail"
        ? "w-full h-full overflow-hidden bg-black flex items-center justify-center rounded-xl"
        : "w-full h-[260px] sm:h-[340px] md:h-[420px] overflow-hidden bg-black flex items-center justify-center rounded-2xl";
    const mediaFrameClass =
      variant === "thumbnail" ? thumbnailFrameClass : postFrameClass;

    return isVideo ? (
      <div key={`${postId}-${index}`} className={videoFrameClass}>
        <video
          src={file.fileUrl}
          title={file.fileName || `Video ${index + 1}`}
          className="w-full h-full object-contain rounded-2xl"
          controls
          aria-label={`Video ${index + 1} for post ${postId}`}
        />
      </div>
    ) : (
      <div key={`${postId}-${index}`} className={mediaFrameClass}>
        <img
          src={file.fileUrl}
          alt={file.fileName || `Media ${index + 1}`}
          className={
            variant === "thumbnail"
              ? "w-full h-full object-cover"
              : "w-full max-h-[520px] object-cover rounded-2xl"
          }
          style={{ display: "block" }}
          onError={(e) => {
            e.currentTarget.src = "https://via.placeholder.com/300";
          }}
        />
      </div>
    );
  };

  return (
    <div className="bg-white rounded-3xl shadow-[0px_2px_12px_0px_rgba(0,0,0,0.04)] border border-gray-100 p-5 space-y-4 hover:shadow-[0px_4px_16px_0px_rgba(0,0,0,0.06)] transition-all">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => onViewProfile(post.profile.id)}
          className="flex items-center gap-3 text-left rounded-md outline-none focus:outline-none focus:ring-0 group"
          aria-label={`${t("viewProfile", "View profile")} ${post.profile.firstName} ${post.profile.lastName}`}
        >
          <div className="relative w-10 h-10 shrink-0">
            {post.profile.profileImage ? (
              <img
                src={post.profile.profileImage}
                alt={`${post.profile.firstName} ${post.profile.lastName}'s profile`}
                className="w-10 h-10 rounded-full object-cover ring-1 ring-gray-100"
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-600 to-indigo-700 flex items-center justify-center text-white font-bold text-xs uppercase shadow-sm">
                {`${post.profile.firstName?.[0] || ""}${post.profile.lastName?.[0] || ""}`}
              </div>
            )}
            {post.profile.verified && (
              <span
                className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-white rounded-full overflow-hidden"
                aria-label="Verified user"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 48 48"
                  className="w-full h-full"
                >
                  <polygon
                    fill="#42a5f5"
                    points="29.62,3 33.053,8.308 39.367,8.624 39.686,14.937 44.997,18.367 42.116,23.995 45,29.62 39.692,33.053 39.376,39.367 33.063,39.686 29.633,44.997 24.005,42.116 18.38,45 14.947,39.692 8.633,39.376 8.314,33.063 3.003,29.633 5.884,24.005 3,18.38 8.308,14.947 8.624,8.633 14.937,8.314 18.367,3.003 23.995,5.884"
                  />
                  <polygon
                    fill="#fff"
                    points="21.396,31.255 14.899,24.76 17.021,22.639 21.428,27.046 30.996,17.772 33.084,19.926"
                  />
                </svg>
              </span>
            )}
          </div>
          <div>
            <h3 className="font-semibold text-start text-sm text-gray-900 group-hover:text-blue-600 transition-colors">
              {post.profile.firstName} {post.profile.lastName}
            </h3>
            <p className="text-xs text-gray-400 mt-0.5">
              {formatDateTime(post.createdAt)}
            </p>
          </div>
        </button>

        {((isMyPost && activeTab === "my") ||
          (!isMyPost && activeTab === "all")) && (
          <div className="relative">
            {showOptionsMenu === post.id && (
              <div
                className="fixed inset-0 z-10"
                onClick={() => onSetShowOptionsMenu(null)}
              />
            )}
            {post.reported === false && (
              <button
                onClick={() =>
                  onSetShowOptionsMenu(
                    showOptionsMenu === post.id ? null : post.id,
                  )
                }
                className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-full"
                aria-label={t("postOptions", "Post options")}
              >
                <FiMoreHorizontal className="w-5 h-5" />
              </button>
            )}
            {showOptionsMenu === post.id && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-2xl shadow-xl border border-gray-100 py-1.5 z-20 overflow-hidden">
                {isMyPost && activeTab === "my" ? (
                  <>
                    <button
                      onClick={() => onEdit(post)}
                      className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3 transition-colors"
                      aria-label={t("editPost")}
                    >
                      <FiEdit2 className="w-4 h-4 text-blue-600" />
                      {t("editPost")}
                    </button>
                    <button
                      onClick={onDelete}
                      className="w-full px-4 py-2.5 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-3 transition-colors"
                      aria-label={t("deletePost")}
                    >
                      <FiTrash2 className="w-4 h-4" />
                      {t("deletePost")}
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => onOpenReportModal(post.id)}
                    className="w-full px-4 py-2.5 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-3 transition-colors"
                    aria-label={t("reportPost", "Report Post")}
                  >
                    <FiFlag className="w-4 h-4" />
                    {t("reportPost", "Report Post")}
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Body / Content */}
      <div>
        {editingPost?.postId === post.id ? (
          <div className="space-y-3">
            {editingPost.error && (
              <p className="text-sm text-red-600" role="alert">
                {editingPost.error}
              </p>
            )}
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">
                {t("media", "Media")}
              </p>
              <div className="grid grid-cols-3 gap-2">
                {!editingPost.clearMedia &&
                  files?.map((file, idx) => (
                    <div
                      key={`old-${idx}`}
                      className="relative group aspect-square overflow-hidden rounded-xl"
                    >
                      {renderMedia(file, idx, post.id, "thumbnail")}
                      <button
                        onClick={() => onRemoveSingleMedia(idx)}
                        className="absolute top-1 right-1 bg-red-500 hover:bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
                        aria-label={t("removeMedia", "Remove media")}
                      >
                        <FiX className="w-4 h-4" />
                      </button>
                    </div>
                  ))}

                {editingPost.newFiles.slice(0, 1).map((file, idx) => {
                  const oldFilesCount = editingPost.clearMedia
                    ? 0
                    : files.length;
                  return (
                    <div
                      key={`new-${idx}`}
                      className="relative group aspect-square overflow-hidden rounded-xl"
                    >
                      {file.type.startsWith("video/") ? (
                        <video
                          src={URL.createObjectURL(file)}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <img
                          src={URL.createObjectURL(file)}
                          alt="preview"
                          className="w-full h-full object-cover"
                        />
                      )}
                      <button
                        onClick={() => onRemoveSingleMedia(oldFilesCount + idx)}
                        className="absolute top-1 right-1 bg-red-500 hover:bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center shadow-lg"
                        aria-label={t("removeMedia", "Remove media")}
                      >
                        <FiX className="w-4 h-4" />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">
                {t("title")}
              </label>
              <input
                type="text"
                value={editingPost.title}
                onChange={(e) =>
                  onSetEditingPost((prev) =>
                    prev ? { ...prev, title: e.target.value } : null,
                  )
                }
                className="w-full mt-1 px-3 py-2 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">
                {t("content")}
              </label>
              <textarea
                value={editingPost.content}
                onChange={(e) =>
                  onSetEditingPost((prev) =>
                    prev ? { ...prev, content: e.target.value } : null,
                  )
                }
                className="w-full mt-1 px-3 py-2 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 resize-none h-24"
              />
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => onSetEditingPost(null)}
                className="px-3 py-1.5 rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm"
                aria-label={t("cancel")}
              >
                {t("cancel")}
              </button>
              <button
                onClick={() => onSavePost(post.id)}
                className="px-3 py-1.5 rounded-xl bg-blue-600 text-white hover:bg-blue-700 text-sm shadow-sm"
                aria-label={t("save")}
              >
                {t("save")}
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-1.5">
            {post.title && (
              <h4 className="font-bold text-base text-start text-gray-900 break-words">
                {post.title}
              </h4>
            )}
            {post.content && (
              <p className="text-gray-800 text-sm text-start leading-relaxed whitespace-pre-wrap break-words">
                {renderFormattedContent(post.content)}
              </p>
            )}
          </div>
        )}
      </div>

      {files.length > 0 && renderMedia(files[0], 0, post.id)}

      {eventDetails && (
        <div className="p-4 rounded-2xl border border-gray-100 bg-gray-50/70">
          <div className="grid grid-cols-3 gap-5">
            <div className="flex min-w-0 items-start gap-2">
              <FiCalendar className="h-4 w-4 shrink-0 text-blue-600" />
              <div className="min-w-0 text-left">
                <p className="text-xs font-medium text-gray-500">
                  {t("event", "Event")}
                </p>
                <p className="truncate text-xs font-semibold text-gray-900">
                  {eventDetails.name || t("notAvailable", "N/A")}
                </p>
              </div>
            </div>

            <div className="flex min-w-0 items-start gap-2">
              <FiMapPin className="h-4 w-4 shrink-0 text-gray-400" />
              <div className="min-w-0 text-left">
                <p className="text-xs font-medium text-gray-500">
                  {t("venue", "Venue")}
                </p>
                <p className="truncate text-xs font-semibold text-gray-900">
                  {eventLocation || t("notAvailable", "N/A")}
                </p>
              </div>
            </div>

            <div className="flex min-w-0 items-start gap-2">
              <FiCalendar className="h-4 w-4 shrink-0 text-gray-400" />
              <div className="min-w-0 text-left">
                <p className="text-xs font-medium text-gray-500">
                  {t("date", "Date")}
                </p>
                <p className="truncate text-xs font-semibold text-gray-900">
                  {eventDetails.startDate
                    ? formatDateTime(eventDetails.startDate)
                    : t("notAvailable", "N/A")}
                </p>
              </div>
            </div>
          </div>

          {eventDetails.bouts && eventDetails.bouts.length > 0 && (
            <div className="mt-4">
              <div className="mb-3 flex items-center justify-between">
                <p className="text-xs font-semibold text-gray-900">
                  {t("matchups", "Matchups")}
                </p>

                {eventBoutCount > visibleEventBoutCount && (
                  <span className="text-[11px] font-medium text-blue-600">
                    +{eventBoutCount - visibleEventBoutCount}{" "}
                    {t("moreBouts", "more bouts")}
                  </span>
                )}
              </div>

              <div className="space-y-2">
                {eventDetails.bouts.slice(0, 2).map((bout, index) => {
                  const redFighterName = getFighterName(
                    bout.redCorner,
                    bout.redCornerFighterName,
                  );
                  const blueFighterName = getFighterName(
                    bout.blueCorner,
                    bout.blueCornerFighterName,
                  );

                  return (
                    <div
                      key={bout.id}
                      className="rounded-xl border border-gray-100 bg-white px-3 py-2.5 shadow-sm"
                    >
                      <div className="mb-2 flex items-center justify-between">
                        <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                          {t("bout", "Bout")} {bout.boutOrder || index + 1}
                        </span>
                      </div>

                      <div className="flex items-center gap-2.5">
                        <div className="flex min-w-0 flex-1 items-center gap-2">
                          {bout.redCorner?.profileImage ? (
                            <img
                              src={bout.redCorner.profileImage}
                              alt={redFighterName}
                              className="h-8 w-8 shrink-0 rounded-full object-cover ring-2 ring-white"
                            />
                          ) : (
                            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-200 text-[10px] font-bold text-gray-500">
                              {redFighterName
                                .split(" ")
                                .map((name) => name[0])
                                .join("")
                                .slice(0, 2)
                                .toUpperCase()}
                            </div>
                          )}

                          <span className="truncate text-xs font-semibold text-gray-800">
                            {redFighterName}
                          </span>
                        </div>

                        <span className="shrink-0 text-[10px] font-bold text-gray-400">
                          VS
                        </span>

                        <div className="flex min-w-0 flex-1 items-center justify-end gap-2">
                          <span className="truncate text-right text-xs font-semibold text-gray-800">
                            {blueFighterName}
                          </span>

                          {bout.blueCorner?.profileImage ? (
                            <img
                              src={bout.blueCorner.profileImage}
                              alt={blueFighterName}
                              className="h-8 w-8 shrink-0 rounded-full object-cover ring-2 ring-white"
                            />
                          ) : (
                            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-200 text-[10px] font-bold text-gray-500">
                              {blueFighterName
                                .split(" ")
                                .map((name) => name[0])
                                .join("")
                                .slice(0, 2)
                                .toUpperCase()}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Counters row */}
      <div className="flex items-center justify-between pt-2 border-t border-gray-100 text-xs text-gray-500">
        <button
          type="button"
          onClick={() => onShowLikes(post.id)}
          className="flex items-center gap-1.5 hover:text-blue-600 transition-colors cursor-pointer group"
          aria-label={t("showLikes", "Show likes")}
        >
          <div className="w-5 h-5 rounded-full bg-blue-600 flex items-center justify-center text-white shadow-sm">
            <FiThumbsUp className="w-2.5 h-2.5 fill-current" />
          </div>
          <span className="font-medium text-gray-700 group-hover:text-blue-600">
            {totalReactions}
          </span>
        </button>
        {post.totalComments > 0 && (
          <button
            type="button"
            onClick={() => onToggleComments(post.id)}
            className="text-xs text-gray-500 hover:text-blue-600 transition-colors font-medium cursor-pointer"
            aria-label={t("showComments", "Show comments")}
          >
            {post.totalComments}{" "}
            {post.totalComments === 1
              ? t("comment", "comment")
              : t("comments", "comments")}
          </button>
        )}
      </div>

      {/* Action buttons row */}
      <div className="flex items-center justify-between pt-2 border-t border-gray-100">
        <button
          type="button"
          onClick={() => onToggleComments(post.id)}
          className="flex items-center gap-2 px-3 py-1.5 rounded-xl text-gray-600 hover:bg-gray-100 transition-all text-xs sm:text-sm font-semibold cursor-pointer"
          aria-label={t("comment")}
        >
          <FiMessageCircle className="w-4 h-4" />
          <span>{t("comment", "Comment")}</span>
        </button>

        {String(post.profile.id) !== String(currentUserId) && (
          <button
            type="button"
            onClick={() => onLike(post.id, likedPosts[post.id] || false)}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-xl transition-all text-xs sm:text-sm font-semibold cursor-pointer ${
              likedPosts[post.id]
                ? "text-blue-600 bg-blue-50/80 hover:bg-blue-100/80"
                : "text-gray-600 hover:bg-gray-100"
            }`}
            aria-label={
              likedPosts[post.id] ? t("unlike", "Unlike") : t("like")
            }
          >
            <FiThumbsUp
              className={`w-4 h-4 ${likedPosts[post.id] ? "fill-current" : ""}`}
            />
            <span>
              {likedPosts[post.id] ? t("liked", "Liked") : t("like", "Like")}
            </span>
          </button>
        )}
      </div>

      {/* Comments expand section */}
      {hasComments && (
        <div className="pt-3 border-t border-gray-100 space-y-4">
          <div className="flex items-center gap-2.5">
            <input
              type="text"
              value={newComment[post.id] || ""}
              onChange={(e) =>
                onSetNewComment((prev) => ({
                  ...prev,
                  [post.id]: e.target.value,
                }))
              }
              onKeyDown={(e) => {
                if (e.key === "Enter" && newComment[post.id]?.trim()) {
                  onAddComment(e as any, post.id);
                }
              }}
              placeholder={t("addCommentPlaceholder", "Write a comment...")}
              className="flex-1 text-xs sm:text-sm px-4 h-9 bg-gray-100 hover:bg-gray-200/60 focus:bg-white rounded-full border border-transparent focus:border-blue-500 outline-none transition-all"
              aria-label={t("addCommentPlaceholder")}
            />
            <button
              onClick={(e) => onAddComment(e as any, post.id)}
              disabled={!newComment[post.id]?.trim()}
              className={`h-9 w-9 rounded-full flex items-center justify-center transition-all cursor-pointer ${
                newComment[post.id]?.trim()
                  ? "bg-blue-600 text-white hover:bg-blue-700 shadow-sm"
                  : "bg-gray-200 text-gray-400 cursor-not-allowed"
              }`}
              aria-label={t("submitComment", "Submit comment")}
            >
              <FiSend className="w-3.5 h-3.5" />
            </button>
          </div>

          {comments[post.id]?.length > 0 && (
            <div className="space-y-3 mt-3">
              {comments[post.id].map((comment) => {
                const isMyComment =
                  String(comment.profile.id) === String(currentUserId);
                return (
                  <div key={comment.id} className="flex items-start gap-2.5">
                    <button
                      type="button"
                      onClick={() => onViewProfile(comment.profile.id)}
                      className="flex-shrink-0 rounded-full outline-none focus:outline-none focus:ring-0 cursor-pointer"
                      aria-label={`${t("viewProfile", "View profile")} ${comment.profile.firstName} ${comment.profile.lastName}`}
                    >
                      {comment.profile.profileImage ? (
                        <img
                          src={comment.profile.profileImage}
                          alt={`${comment.profile.firstName} ${comment.profile.lastName}'s profile`}
                          className="w-8 h-8 rounded-full object-cover mt-0.5"
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-[10px] uppercase mt-0.5">
                          {`${comment.profile.firstName?.[0] || ""}${comment.profile.lastName?.[0] || ""}`}
                        </div>
                      )}
                    </button>
                    <div className="flex-1">
                      <div className="bg-gray-100/80 text-start rounded-2xl px-3.5 py-2 w-fit max-w-full">
                        <button
                          type="button"
                          onClick={() => onViewProfile(comment.profile.id)}
                          className="font-semibold text-xs text-gray-900 block hover:text-blue-600 outline-none focus:outline-none focus:ring-0 text-left cursor-pointer"
                        >
                          {comment.profile.firstName} {comment.profile.lastName}
                        </button>
                        <span className="text-xs text-start text-gray-800 block mt-0.5 break-words">
                          {comment.content}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 px-2 mt-1">
                        <p className="text-[10px] text-gray-400">
                          {formatDateTime(comment.createdAt)}
                        </p>
                        {isMyComment && (
                          <button
                            onClick={() =>
                              onDeleteComment(post.id, comment.id)
                            }
                            className="text-[10px] font-semibold text-gray-500 hover:text-red-600 cursor-pointer"
                            aria-label={t("deleteComment", "Delete comment")}
                          >
                            {t("delete")}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {comments[post.id]?.length === 0 && (
            <div className="text-center py-4">
              <p className="text-xs text-gray-400">{t("noComments")}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PostCard;
